import { Table as ChakraTable } from '@chakra-ui/react'
import {
  ColumnSizingState,
  PaginationState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  DEFAULT_CONTEXT_MENU_STATE,
  DEFAULT_PAGINATION,
  DataTableBody,
  DataTableBodyCell,
  DataTableBodyRow,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableHeaderGroupRow,
  DataTableLoadingRow,
  DataTableMenus,
  DataTableWrapper,
  calculateAutoColumnSizing,
  createContextMenuState,
  createDefaultColumnMenuState,
  expandColumnSizingToFillWidth,
} from './DataTable.parts'
import { DataTableProps } from './types'

export const DataTable = <TData extends Record<string, any>>({
  data,
  columns,
  manualPagination,
  pageCount,
  pagination,
  onPaginationChange,
  manualSorting,
  sorting,
  onSortingChange,
  manualFiltering,
  isLoading,
  minHeight = '335px',
  height,
  defaultMaxAutoWidth = 400,
  enableRowSelection,
  rowSelection: externalRowSelection,
  onRowSelectionChange: externalOnRowSelectionChange,
}: DataTableProps<TData>) => {
  const [internalSorting, setInternalSorting] = useState<SortingState>([])
  const [internalPagination, setInternalPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION)
  const [internalRowSelection, setInternalRowSelection] = useState<
    import('@tanstack/react-table').RowSelectionState
  >({})
  const [selectionAnchorRowId, setSelectionAnchorRowId] = useState<
    string | null
  >(null)
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [tableWidth, setTableWidth] = useState(0)
  const [contextMenu, setContextMenu] = useState(DEFAULT_CONTEXT_MENU_STATE)
  const [columnMenu, setColumnMenu] = useState(() =>
    createDefaultColumnMenuState<TData>(),
  )

  const activeSorting = sorting !== undefined ? sorting : internalSorting
  const activePagination =
    pagination !== undefined ? pagination : internalPagination
  const activeRowSelection =
    externalRowSelection !== undefined
      ? externalRowSelection
      : internalRowSelection

  const handleRowSelectionChange = useCallback(
    (updater: any) => {
      if (externalOnRowSelectionChange) {
        externalOnRowSelectionChange(
          typeof updater === 'function' ? updater(activeRowSelection) : updater,
        )
      } else {
        setInternalRowSelection(updater)
      }
    },
    [activeRowSelection, externalOnRowSelectionChange],
  )

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      cell: info => {
        const value = info.getValue()
        return value == null || value === '' ? '-' : (value as any)
      },
    },
    state: {
      sorting: activeSorting,
      pagination: activePagination,
      rowSelection: activeRowSelection,
      columnSizing,
      columnVisibility,
    },
    enableRowSelection:
      enableRowSelection !== undefined ? enableRowSelection : true,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: updater => {
      if (onSortingChange) {
        onSortingChange(
          typeof updater === 'function' ? updater(activeSorting) : updater,
        )
      } else {
        setInternalSorting(updater)
      }
    },
    onPaginationChange: updater => {
      if (onPaginationChange) {
        onPaginationChange(
          typeof updater === 'function' ? updater(activePagination) : updater,
        )
      } else {
        setInternalPagination(updater)
      }
    },
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    getPaginationRowModel: manualPagination
      ? undefined
      : getPaginationRowModel(),
    manualPagination,
    pageCount,
    manualSorting,
    manualFiltering,
    columnResizeMode: 'onChange',
    enablePinning: true,
    getRowId: (row, index) => (row as any).id || String(index),
  })

  const handleRowClick = useCallback(
    (
      event: React.MouseEvent,
      row: import('@tanstack/react-table').Row<TData>,
    ) => {
      if (!row.getCanSelect()) return

      const rowId = row.id
      const isShift = event.shiftKey
      const isCtrlCmd = event.ctrlKey || event.metaKey

      if (isShift && selectionAnchorRowId) {
        const { rows } = table.getRowModel()
        const anchorIndex = rows.findIndex(r => r.id === selectionAnchorRowId)
        const currentIndex = rows.findIndex(r => r.id === rowId)

        if (anchorIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(anchorIndex, currentIndex)
          const end = Math.max(anchorIndex, currentIndex)

          const newSelection: import('@tanstack/react-table').RowSelectionState =
            isCtrlCmd ? { ...activeRowSelection } : {}

          for (let i = start; i <= end; i++) {
            const r = rows[i]
            if (r.getCanSelect()) {
              newSelection[r.id] = true
            }
          }

          handleRowSelectionChange(newSelection)
        } else {
          handleRowSelectionChange(
            isCtrlCmd
              ? { ...activeRowSelection, [rowId]: !activeRowSelection[rowId] }
              : { [rowId]: true },
          )
          setSelectionAnchorRowId(rowId)
        }
      } else if (isCtrlCmd) {
        const newSelection = { ...activeRowSelection }
        if (newSelection[rowId]) {
          delete newSelection[rowId]
        } else {
          newSelection[rowId] = true
        }
        handleRowSelectionChange(newSelection)
        setSelectionAnchorRowId(rowId)
      } else {
        handleRowSelectionChange({ [rowId]: true })
        setSelectionAnchorRowId(rowId)
      }
    },
    [activeRowSelection, selectionAnchorRowId, table, handleRowSelectionChange],
  )
  const visibleColumnIds = table
    .getVisibleLeafColumns()
    .map(column => column.id)
    .join('|')

  const applyAutosize = useCallback(
    (targetColumnIds?: string[]) => {
      const visibleColumnIds = table
        .getVisibleLeafColumns()
        .map(column => column.id)
      const sizing = calculateAutoColumnSizing({
        columns,
        data,
        defaultMaxAutoWidth,
        useData: data.length > 0,
        columnIds: targetColumnIds,
      })

      setColumnSizing(currentSizing =>
        expandColumnSizingToFillWidth({
          sizing: targetColumnIds ? { ...currentSizing, ...sizing } : sizing,
          columnIds: visibleColumnIds,
          containerWidth: tableWidth,
        }),
      )
    },
    [columns, data, defaultMaxAutoWidth, table, tableWidth],
  )

  useEffect(() => {
    const tableContainer = tableContainerRef.current

    if (!tableContainer) {
      return
    }

    const updateWidth = () => {
      setTableWidth(tableContainer.clientWidth)
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(tableContainer)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!columns.length || tableWidth <= 0) {
      return
    }

    applyAutosize()
  }, [applyAutosize, columns.length, tableWidth, visibleColumnIds])

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 44, // Default row height
    overscan: 20,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()

  const handleContextMenu = useCallback((event, cell) => {
    event.preventDefault()
    setContextMenu(createContextMenuState(event, cell))
  }, [])

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isOpen: false }))
  }, [])

  const copyContextCell = useCallback(() => {
    navigator.clipboard.writeText(contextMenu.cellText)
    closeContextMenu()
  }, [closeContextMenu, contextMenu.cellText])

  const copyContextRow = useCallback(() => {
    navigator.clipboard.writeText(contextMenu.rowText)
    closeContextMenu()
  }, [closeContextMenu, contextMenu.rowText])

  const openColumnMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, column: { id: string }) => {
      const rect = event.currentTarget.getBoundingClientRect()

      setColumnMenu({
        isOpen: true,
        x: rect.left,
        y: rect.bottom + 4,
        columnId: column.id,
      })
    },
    [],
  )

  const closeColumnMenu = useCallback(() => {
    setColumnMenu(prev => ({ ...prev, isOpen: false }))
  }, [])

  const autosizeColumn = useCallback(
    (columnId: string) => {
      applyAutosize([columnId])
    },
    [applyAutosize],
  )

  const resetColumns = useCallback(() => {
    table.resetColumnOrder()
    table.resetColumnPinning()
    applyAutosize()
  }, [applyAutosize, table])

  return (
    <DataTableWrapper
      ref={tableContainerRef}
      minHeight={minHeight}
      height={height}
    >
      <ChakraTable variant="simple" size="sm" style={{ display: 'grid' }}>
        <DataTableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <DataTableHeaderGroupRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <DataTableHeaderCell
                  key={header.id}
                  header={header}
                  index={index}
                  columnCount={headerGroup.headers.length}
                  onOpenColumnMenu={openColumnMenu}
                />
              ))}
            </DataTableHeaderGroupRow>
          ))}
        </DataTableHeader>
        <DataTableBody totalSize={rowVirtualizer.getTotalSize()}>
          {isLoading ? (
            <DataTableLoadingRow table={table} />
          ) : (
            virtualRows.map(virtualRow => {
              const row = rows[virtualRow.index]

              return (
                <DataTableBodyRow
                  key={row.id}
                  row={row}
                  virtualRow={virtualRow}
                  measureElement={rowVirtualizer.measureElement}
                  onRowClick={handleRowClick}
                >
                  {row.getVisibleCells().map(cell => (
                    <DataTableBodyCell
                      key={cell.id}
                      cell={cell}
                      onContextMenu={handleContextMenu}
                    />
                  ))}
                </DataTableBodyRow>
              )
            })
          )}
        </DataTableBody>
      </ChakraTable>

      <DataTableMenus
        contextMenu={contextMenu}
        onCloseContextMenu={closeContextMenu}
        onCopyCell={copyContextCell}
        onCopyRow={copyContextRow}
        columnMenu={columnMenu}
        onCloseColumnMenu={closeColumnMenu}
        onAutosizeColumn={autosizeColumn}
        onAutosizeAllColumns={applyAutosize}
        onResetColumns={resetColumns}
        table={table}
      />
    </DataTableWrapper>
  )
}
